import React, { useState, useEffect } from 'react';
import { useField } from 'formik';
import {
  Grid,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Dialog,
  Slider,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import 'dayjs/locale/de';

const Terminierung = ({ formikProps }) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { setFieldValue, values } = formikProps;
  const [openDatePickers, setOpenDatePickers] = useState({ 1: false, 2: false, 3: false });
  const [dringlichkeitDAYS] = useField('dringlichkeitDAYS');

  // frühestes Datum: max(heute + dringlichkeitDAYS, 07.01.2025), auf Tagesbeginn normalisiert
  const minSelectableDate = (() => {
    const calculated = dayjs().add(Number(dringlichkeitDAYS.value || 0), 'day').startOf('day');
    const absoluteMin = dayjs('2025-01-07').startOf('day');
    return calculated.isAfter(absoluteMin) ? calculated : absoluteMin;
  })();

  const isWeekend = (date) => {
    const d = dayjs(date).day();
    return d === 0 || d === 6;
  };

  // 08:00–18:00 in 15-Minuten-Schritten
  const timeLabels = Array.from({ length: 41 }, (_, i) =>
    dayjs().startOf('day').add(8 * 60 + i * 15, 'minute').format('HH:mm')
  );

  const terminFields = [
    { label: 'Terminvorschlag 1', field: 'termin1', sliderField: 'termin1_slider', index: 1 },
    { label: 'Terminvorschlag 2', field: 'termin2', sliderField: 'termin2_slider', index: 2 },
    { label: 'Terminvorschlag 3', field: 'termin3', sliderField: 'termin3_slider', index: 3 },
  ];

  // "DD.MM.YYYY - HH:MM" -> [dateStr, minutesSinceMidnight]
  const parseTime = (str) => {
    const match = /^(\d{2}\.\d{2}\.\d{4}) - (\d{2}):(\d{2})/.exec(str || '');
    if (!match) return [null, null];
    const [, date, hour, minute] = match;
    return [date, parseInt(hour) * 60 + parseInt(minute)];
  };

  // Default Sliderwerte setzen (08:00–11:00), falls leer
  useEffect(() => {
    terminFields.forEach(({ sliderField }) => {
      if (!values[sliderField]) {
        setFieldValue(sliderField, [8 * 60, 11 * 60]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" mb={0}>Bitte wählen Sie 3 Terminvorschläge</Typography>
        <Typography variant="body1" pb={5}>
          Unser Handwerkspartner meldet sich bei Ihnen zur genauen Terminabstimmung.
        </Typography>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
        <Grid container spacing={2} alignItems="stretch">
          {terminFields.map(({ label, field, sliderField, index }) => {
            const [datePart] = parseTime(values[field]);
            const currentSlider = values[sliderField] || [8 * 60, 10 * 60];

            const startIndex = (currentSlider[0] - 8 * 60) / 15;
            const endIndex = (currentSlider[1] - 8 * 60) / 15;

            return (
              <Grid item xs={12} md={4} key={index} sx={{ display: 'flex' }}>
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: '#E7EBF0',
                    boxShadow: 1,
                    '&:hover': { boxShadow: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                  }}
                >
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Typography variant="h6">📅 {label}</Typography>

                    {/* Datumsauswahl (öffnet Dialog) */}
                    <Box
                      sx={{ mt: 2, cursor: 'pointer' }}
                      onClick={() => setOpenDatePickers((prev) => ({ ...prev, [index]: true }))}
                    >
                      <TextField
                        label="Datum"
                        required 
                        placeholder="Datum wählen"
                        value={datePart || ''}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarMonthIcon />
                            </InputAdornment>
                          ),
                          sx: { '& .MuiInputBase-input': { cursor: 'pointer' } },
                        }}
                      />
                    </Box>

                    <Dialog
                      fullScreen={isSmDown}
                      fullWidth
                      maxWidth="xs"
                      open={openDatePickers[index]}
                      onClose={() => setOpenDatePickers((prev) => ({ ...prev, [index]: false }))}
                    >
                      <Box sx={{ p: 2 }}>
                        <DateCalendar
                          value={datePart ? dayjs(datePart, 'DD.MM.YYYY') : null}
                          onChange={(newDate) => {
                            if (!newDate) return;

                            // Sicherheits-Check: nichts vor minSelectableDate erlauben
                            const picked = dayjs(newDate).startOf('day');
                            if (picked.isBefore(minSelectableDate) || isWeekend(picked)) {
                              return;
                            }

                            const startTime = dayjs()
                              .startOf('day')
                              .add(currentSlider[0], 'minute')
                              .format('HH:mm');

                            const value = picked.format('DD.MM.YYYY') + ' - ' + startTime + ':00';
                            setFieldValue(field, value);
                            setOpenDatePickers((prev) => ({ ...prev, [index]: false }));
                          }}
                          // doppelt absichern: UI-seitig und programmatisch
                          minDate={minSelectableDate}
                          shouldDisableDate={(date) =>
                            isWeekend(date) || dayjs(date).startOf('day').isBefore(minSelectableDate)
                          }
                        />
                      </Box>
                    </Dialog>

                    {/* Zeitfenster (15-Minuten-Takt) */}
                    <Box sx={{ mt: 4, px: 2, overflow: 'visible' }}>
                      <Slider
                        value={[startIndex, endIndex]}
                        onChange={(_, newValue) => {
                          const [start, end] = newValue;
                          const selectedDate = datePart || '';
                          const startTime = timeLabels[start];

                          const startMinutes = 8 * 60 + start * 15;
                          const endMinutes = 8 * 60 + end * 15;

                          setFieldValue(sliderField, [startMinutes, endMinutes]);
                          if (selectedDate) {
                            setFieldValue(field, `${selectedDate} - ${startTime}:00`);
                          }
                        }}
                        step={1}                // 1 Schritt = 15 Minuten
                        marks                   // Markierungen alle 15 Minuten
                        min={0}
                        max={timeLabels.length - 1}
                        valueLabelDisplay="on"
                        valueLabelFormat={(val) => timeLabels[val]}
                      />
                      <Typography variant="body2" align="center" mt={1}>
                        Gewählter Bereich: {timeLabels[startIndex]} – {timeLabels[endIndex]}
                      </Typography>
                    </Box>

                    <Box sx={{ flexGrow: 1 }} />
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </LocalizationProvider>
    </Box>
  );
};

export default Terminierung;
